import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlexBox } from '@vp/swan';
import { StarEmptySvg } from './StarEmptySvg';
import { StarFilledSvg } from './StarFilledSvg';
import { StarRatingTooltip } from './StarRatingTooltip';

type StarRatingSize = 'standard' | 'small';

export interface StarRatingProps {
    /** Controlled value (1–totalStars). Omit or pass undefined for uncontrolled. */
    value?: number | null;
    /** Initial value for uncontrolled mode. */
    defaultValue?: number;
    /** Called when the selected rating changes. */
    onChange?: (value: number) => void;
    /** Called when the hovered star changes. Receives null when the pointer leaves. */
    onChangeActive?: (value: number | null) => void;
    /** Radio group name attribute, used as the accessible group label. Default "Rating". */
    name?: string;
    /** Maximum number of stars. Default 5. */
    totalStars?: number;
    /** Disables all interaction. */
    disabled?: boolean;
    /** Marks the field as required for form validation. */
    required?: boolean;
    /** Size variant. Default "standard". */
    size?: StarRatingSize;
    /** Per-star label map, e.g. { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' }. */
    labels?: Record<number, string>;
    /** Show a tooltip above the hovered or focused star when labels are provided. Default false. */
    showTooltip?: boolean;
}

const touchTargetSize: Record<StarRatingSize, string> = {
    standard: '44px',
    small: '24px',
};

export const StarRating = ({
    value,
    defaultValue,
    onChange,
    onChangeActive,
    name = 'Rating',
    totalStars = 5,
    disabled = false,
    required = false,
    size = 'standard',
    labels,
    showTooltip = false,
}: StarRatingProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue ?? 0);
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    const [focusedValue, setFocusedValue] = useState<number | null>(null);
    const isKeyboardRef = useRef(true);
    const groupRef = useRef<HTMLDivElement>(null);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value ?? 0 : internalValue;
    const activeValue = hoverValue ?? currentValue;
    // Hover takes priority over focus for tooltip target
    const tooltipTarget = hoverValue ?? focusedValue;

    const handleSelect = useCallback(
        (newValue: number) => {
            if (disabled) return;
            if (!isControlled) setInternalValue(newValue);
            onChange?.(newValue);
        },
        [disabled, isControlled, onChange]
    );

    useEffect(() => {
        const onKey = () => {
            isKeyboardRef.current = true;
        };
        const onPointer = () => {
            isKeyboardRef.current = false;
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('pointerdown', onPointer, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('pointerdown', onPointer, true);
        };
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) return;

            let newValue = currentValue;
            switch (e.key) {
                case 'ArrowRight':
                case 'ArrowUp':
                    e.preventDefault();
                    newValue = Math.min(currentValue + 1, totalStars);
                    break;
                case 'ArrowLeft':
                case 'ArrowDown':
                    e.preventDefault();
                    newValue = Math.max(currentValue - 1, 1);
                    break;
                case ' ':
                case 'Enter': {
                    e.preventDefault();
                    const focused = groupRef.current?.querySelector<HTMLInputElement>(
                        'input[type="radio"]:focus'
                    );
                    if (focused) handleSelect(Number(focused.value));
                    return;
                }
                default:
                    return;
            }

            if (newValue !== currentValue) {
                handleSelect(newValue);
                const radios =
                    groupRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]');
                radios?.[newValue - 1]?.focus();
            }
        },
        [disabled, currentValue, totalStars, handleSelect]
    );

    const handleMouseEnter = useCallback(
        (starNumber: number) => {
            setHoverValue(starNumber);
            onChangeActive?.(starNumber);
        },
        [onChangeActive]
    );

    const handleMouseLeave = useCallback(() => {
        setHoverValue(null);
        onChangeActive?.(null);
    }, [onChangeActive]);

    return (
        <FlexBox
            ref={groupRef as React.Ref<HTMLElement>}
            role="radiogroup"
            aria-label={name}
            aria-disabled={disabled || undefined}
            aria-required={required || undefined}
            gap="3"
            alignItems="center"
            justifyContent="center"
            onKeyDown={handleKeyDown}
            onMouseLeave={handleMouseLeave}
        >
            {Array.from({ length: totalStars }, (_, i) => {
                const starNumber = i + 1;
                const isFilled = activeValue >= starNumber;
                const isChecked = starNumber === currentValue;
                const ariaLabel = labels?.[starNumber] ?? `${starNumber}`;
                const tabIndex = isChecked || (currentValue === 0 && starNumber === 1) ? 0 : -1;
                const isTooltipVisible = showTooltip && tooltipTarget === starNumber;

                return (
                    <span
                        key={starNumber}
                        onMouseEnter={() => handleMouseEnter(starNumber)}
                        style={{
                            position: 'relative',
                            display: 'inline-flex',
                            lineHeight: 0,
                            transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                            transform:
                                !disabled && hoverValue === starNumber ? 'scale(1.2)' : 'scale(1)',
                            opacity: disabled ? 0.5 : 1,
                            ...(focusedValue === starNumber
                                ? {
                                      boxShadow: 'var(--swan-sem-focus-standard)',
                                      borderRadius: 'var(--swan-sem-border-radius-focus)',
                                  }
                                : {}),
                        }}
                    >
                        <input
                            type="radio"
                            name={name}
                            value={starNumber}
                            checked={isChecked}
                            disabled={disabled}
                            aria-label={ariaLabel}
                            tabIndex={tabIndex}
                            onChange={() => handleSelect(starNumber)}
                            onFocus={() => {
                                if (isKeyboardRef.current) setFocusedValue(starNumber);
                            }}
                            onBlur={() => setFocusedValue(null)}
                            style={{
                                position: 'absolute',
                                width: touchTargetSize[size],
                                height: touchTargetSize[size],
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                opacity: 0,
                                margin: 0,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                zIndex: 1,
                            }}
                        />
                        {isFilled ? <StarFilledSvg size={size} /> : <StarEmptySvg size={size} />}
                        {isTooltipVisible && labels?.[starNumber] && (
                            <StarRatingTooltip id={`${name}-tooltip-${starNumber}`}>
                                {labels[starNumber]}
                            </StarRatingTooltip>
                        )}
                    </span>
                );
            })}
        </FlexBox>
    );
};
